import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import NavMark, { type NavMarkName } from '../components/brand/NavMark';
import '../styles/desk.css';
import '../styles/workspace.css';

type NavItem = { name: string; href: string; paths: string[]; tone: 'land' | 'run' | 'keep'; mark: NavMarkName };

/**
 * Three folders — one home for each job, and one way to reach it.
 *
 * Dashboard and Search are the free half: your channel and everything on it (sponsors,
 * shop), and looking up any creator or brand — the data every user should be building anyway.
 * Bearri is the paid half, and the agent personified: every deal (pitching, replies, offers,
 * counters) worked through with Bearri, on one page that shows what it can see.
 *
 * It used to be seven tabs, then four: the Deals list and the assistant were two pages about the
 * same deals, and the assistant lost its tab altogether when the dashboard became home. A page
 * with no folder is a page nobody finds — so nothing here hides: a route without a tab is a step
 * inside one (/app/deals/:id and /app/pitch-writer open from a deal in Bearri; scouting your own
 * channel is a Bearri tool now, /app/bearri?scout=, and the old /app/prospects files under Bearri).
 *
 * `paths` is every route that files under a folder, so the tab stays lit while you are anywhere
 * inside it. Each tab carries a mark as well as a name: at a glance you find the folder by its
 * shape, and only read the label to confirm it. The mark is stamped, not drawn — see NavMark.
 *
 * Each folder is its own chapter, so each has its own tone (desk.css TONES): it tints the mark
 * on the board, edges the tab when lifted, and is the colour of that page's eyebrow once open.
 * Three folders in one blue told you nothing about which one you were in.
 */
const NAV: NavItem[] = [
  { name: 'Dashboard', href: '/app/dashboard', tone: 'keep', mark: 'board',
    paths: ['/app/dashboard', '/app/activity'] },
  // Search lands on the creator search: looking someone up is the common case and the one
  // people arrive wanting. ScoutSwitch — the folio's tabs — crosses to brands and to what you
  // saved. (It was called Scout; "Search" says what it is.)
  { name: 'Search', href: '/app/discover', tone: 'land', mark: 'lens',
    paths: ['/app/discover', '/app/sponsor-intel', '/app/creator'] },
  // Named for the agent, not the work: the tab where Bearri works with you. Its mark is a paw
  // print — what Bearri signs with — not a second bear face beside the logo's.
  { name: 'Bearri', href: '/app/bearri', tone: 'run', mark: 'paw',
    paths: ['/app/bearri', '/app/deals', '/app/pitch-writer', '/app/prospects'] },
];

/**
 * The logo is the berry itself (Bearri = bear + berry), and it is the way home. The one PNG is
 * printed twice and split at the stem: the leaf crown on top, the berry under it. At rest the two
 * layers are one picture; hovered, the leaves shake on their stem while the berry bobs; pressed,
 * the berry squashes and springs back. No second asset — the split is two clips of the same image.
 *
 * On the navy board the berry's navy rim and leaves measured 1.0:1 — they vanished, and so would
 * any wiggle. So the berry is a die-cut sticker: a paper edge cut around the whole silhouette (the
 * filter below), drawn over the moving layers so the edge follows the leaves as they shake.
 */
function BerryLogo() {
  return (
    <Link to="/app/dashboard" className="br-workspace-logo" aria-label="Bearri — go to your dashboard">
      <svg className="br-berry__defs" width="0" height="0" aria-hidden="true" focusable="false">
        <filter id="br-berry-cut" x="-20%" y="-20%" width="140%" height="140%" colorInterpolationFilters="sRGB">
          <feMorphology in="SourceAlpha" operator="dilate" radius="1.25" result="grown" />
          <feFlood className="br-berry__edge" />
          <feComposite in2="grown" operator="in" result="cut" />
          <feMerge><feMergeNode in="cut" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </svg>
      <span className="br-berry" aria-hidden="true">
        <img className="br-berry__fruit" src="/logo.png" alt="" draggable={false} />
        <img className="br-berry__leaves" src="/logo.png" alt="" draggable={false} />
      </span>
    </Link>
  );
}

/**
 * Pages that ARE the room rather than a column on the desk: Bearri's drawer and work run the full
 * width (1376px + 32px gutters), with no site footer under them — the drawer runs to the bottom of
 * the window and the page scrolls. Only /app/bearri, so far.
 */
const BLEED_PATHS = ['/app/bearri'];
export const isBleedPath = (pathname: string) => BLEED_PATHS.includes(pathname);

/**
 * The same folder navigation is visible with a keyboard, a mouse, or a phone. It is one bar: the
 * berry, the folders hanging from the bar's bottom edge, and your own tools (the bell, and your
 * portrait, which is the way to your profile and settings).
 *
 * `bleed` makes the page the room (see BLEED_PATHS): the full-width main and no footer. Left out,
 * it follows the active path, so a dev preview lit as /app/bearri bleeds the same way.
 */
export default function WorkspaceShell({children, account, banner, activePath, bleed}: {
  children:React.ReactNode; account?:React.ReactNode; banner?:React.ReactNode; activePath?:string; bleed?:boolean;
}) {
  const location = useLocation();
  const pathname = activePath || location.pathname;
  const room = bleed ?? isBleedPath(pathname);
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = navRef.current;
    const active = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (nav && active) nav.scrollLeft = Math.max(0, active.offsetLeft - nav.offsetLeft - 16);
  }, [pathname]);
  return (
    <div className="br-workspace" data-bleed={room ? 'true' : undefined}>
      <a href="#workspace-content" className="sr-only focus:not-sr-only focus:p-3">Skip to content</a>
      <header className="br-workspace-header" data-no-print>
        <div className="br-workspace-bar">
          <BerryLogo />
          <nav ref={navRef} className="br-workspace-nav" aria-label="Creator workspace">
            {NAV.map(item => {
              const active = item.paths.some(p => pathname === p || pathname.startsWith(p + '/'));
              return (
                <Link key={item.href} to={item.href} data-tone={item.tone} aria-current={active ? 'page' : undefined}>
                  <NavMark name={item.mark} />
                  {/* on a phone only the open folder spells its name; the rest keep it for screen readers */}
                  <span className="br-workspace-nav__label">{item.name}</span>
                </Link>
              );
            })}
          </nav>
          <div className="br-workspace-tools">{account}</div>
        </div>
      </header>
      {banner}
      <main id="workspace-content" className="br-workspace-main">{children}</main>
      {!room && <footer className="br-workspace-footer" data-no-print>
        <div><span>© {new Date().getFullYear()} Bearri</span><nav aria-label="Workspace footer">
          {/* /app/plan is where people pay, so the Terms sit one click from every page that isn't the room */}
          <Link to="/about">About</Link><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link>
          <a href="mailto:support@bearri.co">Get help</a>
        </nav></div>
      </footer>}
    </div>
  );
}
